/**
 * The app setup graph — every provisioning step for ONE app as a dependency graph, and the
 * "run full setup" drive over it (SCH-2236).
 *
 * GET  /v1/mobile-apps/:id/setup/graph                   (super-admin)
 * POST /v1/mobile-apps/:id/setup/run-all                 (super-admin) start, join or resume
 * POST /v1/mobile-apps/:id/setup/nodes/:nodeId/run       (super-admin) Run / Retry one node
 * POST /v1/mobile-apps/:id/setup/nodes/:nodeId/resume    (super-admin) re-run it, then the rest
 * POST /v1/mobile-apps/:id/setup/nodes/:nodeId/mark      (super-admin) Mark done / Skip
 *
 * The nodes are the Apple and Android checklist rows plus the steps that used to live on their own
 * panels (Expo project, EAS environment, submit key, OneSignal, Firebase apps, store screenshots,
 * builds). Each node's state is read from the record its own step already writes; the graph adds
 * only the edges between them and the run-all record that says which nodes a drive has attempted.
 *
 * The node ids are listed here rather than imported from the Apple/Android enums in `./index`,
 * which re-exports this file — the backend catalog asserts the two stay in step.
 */

import { z } from 'zod'
import { SetupLastRunSchema } from './setup-last-run'

export const SETUP_GRAPH_LANES = ['apple', 'google', 'expo', 'assets', 'calls', 'ship'] as const
export const SetupGraphLaneSchema = z.enum(SETUP_GRAPH_LANES)
export type SetupGraphLane = z.infer<typeof SetupGraphLaneSchema>

export const SETUP_GRAPH_NODE_IDS = [
  // Apple checklist rows
  'apple.identifiers',
  'apple.asc-app',
  'apple.app-group',
  'apple.apns-key',
  'apple.app-info',
  'apple.age-rating',
  'apple.privacy',
  'apple.pricing',
  'apple.listing',
  'apple.screenshots',
  'apple.review-info',
  'apple.release-option',
  'apple.reset-ratings',
  'apple.submit-review',
  'apple.ios-cert',
  // Android checklist rows
  'play.shell',
  'play.listing-api',
  'play.graphics-api',
  'play.app-content',
  'play.store-settings',
  'play.countries',
  'play.permission-declarations',
  'play.first-release',
  'play.managed-publishing',
  'firebase.push',
  // Steps that live on their own panels
  'firebase.apps',
  'expo.project',
  'push.onesignal',
  'expo.env',
  'expo.submit-key',
  'assets.screenshots',
  'build.ios',
  'build.android',
  // Steps that used to happen by hand or out of band (Woodland, 2026-09-25)
  'app.identity',
  'app.activate',
  'app.branding',
  'assets.slots',
  'assets.brand',
  'store.listing',
  'expo.webhooks',
  'observability.rum',
  'apple.asc-webhook',
  'apple.testflight',
  'release.line',
  'release.screenshots',
  'release.compliance-ios',
  'release.compliance-android',
  'play.upload',
  'review.demo-login',
  // After a release: demo login off for the released version (SCH-2929)
  'review.demo-login-clear',
  // A rejection remedy's Play resubmission: runs only in a drive whose scope names it (SCH-2889)
  'play.send-for-review',
  // Calls ringing: production serving the ringing routes. iOS rings through APNs with the app's
  // `.p8` (the `apple.apns-key` row), so there is no per-bundle VoIP certificate or VoIP app.
  'calls.backend-ready',
  // The registry gate every build waits on
  'build.readiness',
  // The iOS halves of push and build readiness, split out so nothing Android waits on Apple
  // (SCH-2767): the APNs channel on the OneSignal app, and the iOS build's own gate
  'push.onesignal-ios',
  'build.readiness-ios'
] as const
export const SetupGraphNodeIdSchema = z.enum(SETUP_GRAPH_NODE_IDS)
export type SetupGraphNodeId = z.infer<typeof SetupGraphNodeIdSchema>

/**
 * The report-output field a runner step sets on a `blocked` verdict when the portal cannot do its
 * work YET rather than refusing it: managed publishing before the app is live on Google Play. The
 * setup graph reads such a run as waiting, not as needing a person. Kept here, not beside the
 * browser-step manifest, so the graph's observer reads it without loading the root barrel. The
 * runner keeps its own copy (it takes no dependency on this package); its contract test holds the
 * two equal.
 */
export const STEP_NOT_YET_AVAILABLE_FIELD = 'notYetAvailable'

/**
 * How a node gets done. `api` finishes in-process; `browser` is handed to the runner; `build` is an
 * EAS build dispatched through GitHub Actions; `delegated` is owned by another system and never run.
 */
export const SetupGraphViaSchema = z.enum(['api', 'browser', 'build', 'delegated'])
export type SetupGraphVia = z.infer<typeof SetupGraphViaSchema>

/**
 * The node's state as the scheduler sees it.
 *
 * - `done` — its record says so. `skipped` — delegated, skipped by a person, or retired from the
 *   catalog; satisfies dependents without running.
 * - `upgradeable` — done, but before the node's current revision: what the step does changed since
 *   it ran here. It still satisfies dependents; only an upgrade runs it again.
 * - `running` — a run is in flight (or a drive launched it and it has not reported yet).
 * - `ready` — every dependency is satisfied and nothing has run it; a drive would launch it now.
 * - `queued` — a drive is active and this node waits on a dependency still to finish.
 * - `waiting` — no drive is active and a dependency is not done yet.
 * - `blocked` — cannot proceed without a person: a dependency failed, the run finished with a
 *   human half still open (`needs_attention`), or the node needs Apple and the app has no Apple
 *   publishing account (team) yet — never run, and not counted as a failure of a drive.
 * - `failed` — its last run failed, or the drive's attempt was refused. `detail` says why.
 * - `stuck` — its run is still marked in flight but has gone past the time a run of its kind takes
 *   (a browser dispatch no runner picked up, an EAS build past an hour). Retry is offered.
 * - `gated` — a store-submission node a drive was not allowed to run.
 */
export const SETUP_GRAPH_NODE_STATUSES = [
  'done',
  'upgradeable',
  'skipped',
  'running',
  'ready',
  'queued',
  'waiting',
  'blocked',
  'failed',
  'stuck',
  'gated'
] as const
export const SetupGraphNodeStatusSchema = z.enum(SETUP_GRAPH_NODE_STATUSES)
export type SetupGraphNodeStatus = z.infer<typeof SetupGraphNodeStatusSchema>

/**
 * Who or what filed a node's last record, so the graph shows work done from the app's tabs too.
 *
 * - `tab` — an operator ran the step from its own tab (Apple, Android, Push, Calls…).
 * - `graph` — the setup graph ran it: a single-node Run or a "Run full setup" drive.
 * - `runner` — a browser-runner dispatch of a checklist row. The runner's reports replace each
 *   step's state, so a row's record cannot say whether a tab or the graph launched it.
 * - `mark` — a person marked the node done or skipped it by hand.
 * - `provisioning` — filed by a provisioning run. Reserved: no node's record is read from one yet.
 * - `null` — not known (a build's release target, whichever surface triggered it).
 */
export const SetupGraphRunSourceSchema = z.enum(['tab', 'graph', 'runner', 'mark', 'provisioning'])
export type SetupGraphRunSource = z.infer<typeof SetupGraphRunSourceSchema>

/** The node's own last run, in the checklists' shape, plus where it came from. */
export const SetupGraphLastRunSchema = SetupLastRunSchema.extend({
  /** The dispatch or release that recorded it — how to find it anywhere else. */
  recordId: z.string().nullable(),
  /** When the record last moved: its finish, or its start while it runs. */
  at: z.string(),
  source: SetupGraphRunSourceSchema.nullable().default(null),
  /** Who filed it (an email), when the record says. */
  by: z.string().nullable().default(null),
  /** True when it finished done, false when it failed or needs a person, null while in flight or skipped. */
  ok: z.boolean().nullable().default(null)
})
export type SetupGraphLastRun = z.infer<typeof SetupGraphLastRunSchema>

/** What the active (or most recent) drive did with this node. */
export const SetupGraphAttemptSchema = z.object({
  at: z.string(),
  /** Set when the step refused before anything ran — a missing precondition, not a failed run. */
  refused: z.string().nullable(),
  /**
   * `launching` — written before the step was called; `launched` — the step answered (handed off,
   * or finished and recorded itself). A `launching` attempt the drive's process did not live to
   * finish is how a restart is reconciled: no record since it means nothing started.
   */
  phase: z.enum(['launching', 'launched']).default('launched')
})
export type SetupGraphAttempt = z.infer<typeof SetupGraphAttemptSchema>

export const SetupGraphNodeSchema = z.object({
  id: SetupGraphNodeIdSchema,
  lane: SetupGraphLaneSchema,
  title: z.string(),
  description: z.string(),
  via: SetupGraphViaSchema,
  deps: z.array(SetupGraphNodeIdSchema),
  status: SetupGraphNodeStatusSchema,
  /** Why the node is in that status — a failure reason, the dependency it waits on. */
  detail: z.string().nullable(),
  /** Puts something in front of a store's reviewers; a drive runs it only when asked to. */
  submission: z.boolean(),
  /** The dashboard confirms before a single-node run. */
  confirmBeforeRun: z.boolean(),
  /** A single-node Run is offered (not delegated, not running). */
  runnable: z.boolean(),
  /**
   * Skip is offered: nothing on the way to a store submission depends on this node, so leaving it
   * undone does not stop the app from shipping.
   */
  skippable: z.boolean(),
  /** Mark done is offered: a step a person can finish by hand (a manual gate, or one needing attention). */
  markable: z.boolean(),
  lastRun: SetupGraphLastRunSchema.nullable(),
  attempt: SetupGraphAttemptSchema.nullable(),
  /**
   * The node's current revision number (2, 3, …) when the step changed after apps first ran it.
   * Absent on a node still at its first behaviour. An upgrade runs a revised node it finds undone.
   */
  revision: z.number().int().optional()
})
export type SetupGraphNode = z.infer<typeof SetupGraphNodeSchema>

/**
 * A "run full setup" drive, as the dashboard shows it.
 *
 * - `running` — a backend holds the drive's lease and is ticking it.
 * - `stuck` — running, but at least one node has gone past its time limit.
 * - `paused` — still open, but no backend holds its lease (the one that did restarted or stopped).
 *   A restarted backend picks up the drives it started on its own; Resume picks it up from anywhere,
 *   exactly where it stopped.
 * - `failed` — ended with nodes that failed or need a person. `complete` — every node done.
 */
export const SetupRunAllStatusSchema = z.enum(['running', 'stuck', 'paused', 'failed', 'complete'])
export type SetupRunAllStatus = z.infer<typeof SetupRunAllStatusSchema>

/** A node that stands between the drive and completion, and why. */
export const SetupRunBlockerSchema = z.object({
  id: SetupGraphNodeIdSchema,
  title: z.string(),
  status: SetupGraphNodeStatusSchema,
  detail: z.string().nullable()
})
export type SetupRunBlocker = z.infer<typeof SetupRunBlockerSchema>

export const SetupRunAllSchema = z.object({
  id: z.string(),
  status: SetupRunAllStatusSchema,
  includeSubmission: z.boolean(),
  /** The git ref the drive's builds are made from. */
  ref: z.string(),
  /** Who started the drive (email), when known. */
  requestedBy: z.string().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  /** The driver's last heartbeat. */
  lastTickAt: z.string(),
  /**
   * The App Store version an upgrade made (or reused) in App Store Connect at its start, which the
   * new iOS build ships under. Absent on any other drive, and on an upgrade without store submission.
   */
  storeVersion: z.string().optional(),
  /**
   * True on an upgrade drive started with `resetRatings`, whose scope runs `apple.reset-ratings` on
   * that App Store version. Absent on every other drive.
   */
  resetRatings: z.boolean().optional(),
  /**
   * The nodes the drive cannot get past on its own: failed, stuck, or waiting on a person. Nodes
   * blocked only because one of these is are not listed — fixing the listed ones releases them.
   */
  blockers: z.array(SetupRunBlockerSchema)
})
export type SetupRunAll = z.infer<typeof SetupRunAllSchema>

export const GetSetupGraphResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    nodes: z.array(SetupGraphNodeSchema),
    runAll: SetupRunAllSchema.nullable(),
    counts: z.record(SetupGraphNodeStatusSchema, z.number()),
    /** The git ref a new drive builds from unless told otherwise. */
    defaultRef: z.string()
  }),
  message: z.string()
})
export type GetSetupGraphResponse = z.infer<typeof GetSetupGraphResponseSchema>

export const StartSetupRunAllRequestSchema = z.object({
  /**
   * Also run the store-submission nodes (App Review, Play first release) and upload builds to the
   * stores. Off by default: those are the irreversible, externally visible steps.
   */
  includeSubmission: z.boolean().default(false),
  /**
   * The git ref builds are made from. Defaults to the backend's release branch (`defaultRef` on the
   * graph read) — not `main`, which can lack the fixes a new app's first build needs. Ignored when
   * the call joins or resumes a drive that already has one.
   */
  ref: z.string().trim().min(1).max(200).optional()
})
export type StartSetupRunAllRequest = z.infer<typeof StartSetupRunAllRequestSchema>

/** POST /:id/setup/nodes/:nodeId/run — the run options, plus the tab Run button's override. */
export const RunSetupNodeRequestSchema = StartSetupRunAllRequestSchema.extend({
  /**
   * Run the node even though a dependency is not done — a tab's Run button, where the person is on
   * the step's own panel. The step still refuses on its own preconditions. Off by default: the
   * graph's Run refuses and names the missing dependency.
   */
  ignoreDeps: z.boolean().default(false),
  /**
   * Which surface asked, filed on the node's run record so the graph can say who ran it: `tab` from
   * a tab's Run button, `graph` (the default) from the graph's own.
   */
  source: z.enum(['tab', 'graph']).default('graph')
})
export type RunSetupNodeRequest = z.infer<typeof RunSetupNodeRequestSchema>

export const StartSetupRunAllResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    runAll: SetupRunAllSchema,
    /** Nodes the first scheduling pass launched. */
    launched: z.array(SetupGraphNodeIdSchema),
    /** True when a drive was already running and this call joined it rather than starting one. */
    joined: z.boolean(),
    /** True when this call picked up a paused drive where it stopped rather than starting one. */
    resumed: z.boolean().default(false)
  }),
  message: z.string()
})
export type StartSetupRunAllResponse = z.infer<typeof StartSetupRunAllResponseSchema>

/**
 * POST /:id/setup/stop — "Stop this drive": closes the open drive so it launches nothing more. Builds,
 * submissions and runner dispatches it already started are not cancelled; they keep going.
 */
export const StopSetupDriveResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    /** The drive as it reads now: closed (`failed`). */
    runAll: SetupRunAllSchema,
    /** Nodes the drive had started that were still going when it stopped; they carry on. */
    inFlight: z.array(SetupGraphNodeIdSchema)
  }),
  message: z.string()
})
export type StopSetupDriveResponse = z.infer<typeof StopSetupDriveResponseSchema>

export const RunSetupNodeResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    nodeId: SetupGraphNodeIdSchema,
    /** False when the step was handed off (browser, build) and is still going. */
    finished: z.boolean(),
    ok: z.boolean(),
    detail: z.string(),
    /** True when a live drive owns the node: the retry was handed to it rather than run here. */
    queued: z.boolean().default(false)
  }),
  message: z.string()
})
export type RunSetupNodeResponse = z.infer<typeof RunSetupNodeResponseSchema>

/** "Resume run from here": re-run this node, then let the drive carry on through everything after it. */
export const ResumeSetupFromNodeResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    nodeId: SetupGraphNodeIdSchema,
    runAll: SetupRunAllSchema,
    /** Nodes whose earlier attempts were cleared so the drive tries them again. */
    reset: z.array(SetupGraphNodeIdSchema)
  }),
  message: z.string()
})
export type ResumeSetupFromNodeResponse = z.infer<typeof ResumeSetupFromNodeResponseSchema>

/**
 * Mark done — a person finished this step by hand (recorded with who and when); or Skip — leave a
 * non-critical step undone. Both satisfy the node's dependents until the node runs again.
 */
export const MarkSetupNodeRequestSchema = z.object({
  action: z.enum(['done', 'skip']),
  note: z.string().trim().max(500).optional()
})
export type MarkSetupNodeRequest = z.infer<typeof MarkSetupNodeRequestSchema>

export const MarkSetupNodeResponseSchema = z.object({
  data: z.object({
    appId: z.string(),
    nodeId: SetupGraphNodeIdSchema,
    action: z.enum(['done', 'skip']),
    by: z.string().nullable(),
    at: z.string()
  }),
  message: z.string()
})
export type MarkSetupNodeResponse = z.infer<typeof MarkSetupNodeResponseSchema>
